import { useState, useEffect, useRef, useCallback } from 'react';
import { isAuthEnabled, loadAmplifyAuth } from '../auth/amplifyRuntime';

const ENABLE_AUTH = isAuthEnabled();

function useAppShell({
  authenticated,
  guest = false,
  preferredTheme = 'dark',
  onThemeChange: onThemePreferenceChange,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [insightsCollapsed, setInsightsCollapsed] = useState(false);
  const [userFirstName, setUserFirstName] = useState(null);
  const menuRef = useRef(null);
  const menuPortalRef = useRef(null);
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || preferredTheme;
  });

  useEffect(() => {
    if (!ENABLE_AUTH || !authenticated || guest) {
      setUserFirstName(null);
      return;
    }

    (async () => {
      try {
        const { fetchUserAttributes } = await loadAmplifyAuth();
        const attrs = await fetchUserAttributes();
        setUserFirstName(attrs.given_name || null);
      } catch (err) {
        console.error('Failed to fetch user attributes:', err);
      }
    })();
  }, [authenticated, guest]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inMenuTrigger = menuRef.current?.contains(event.target);
      const inMenuPortal = menuPortalRef.current?.contains(event.target);
      if (!inMenuTrigger && !inMenuPortal) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const toggleMenu = useCallback(() => {
    setMenuOpen((open) => !open);
  }, []);

  const handleThemeChange = useCallback((newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    onThemePreferenceChange?.(newTheme);
    setMenuOpen(false);
  }, [onThemePreferenceChange]);

  useEffect(() => {
    setTheme(preferredTheme || 'dark');
  }, [preferredTheme]);

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((collapsed) => !collapsed);
  }, []);

  const toggleInsightsCollapsed = useCallback(() => {
    setInsightsCollapsed((collapsed) => {
      if (!collapsed && menuOpen) {
        setMenuOpen(false);
      }
      return !collapsed;
    });
  }, [menuOpen]);

  return {
    menuOpen,
    menuRef,
    menuPortalRef,
    closeMenu,
    toggleMenu,
    theme,
    handleThemeChange,
    sidebarCollapsed,
    toggleSidebarCollapsed,
    insightsCollapsed,
    toggleInsightsCollapsed,
    userFirstName,
  };
}

export default useAppShell;
